import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { HighlightTemplate, SlideLayoutProps } from '../types';
import {
  BodyContent,
  LEADING,
  PADDING,
  SlideText,
  TYPE,
  fitTitle,
  formatPageLabel,
  hasMath,
  hasNextSlide,
  shouldShowCounter,
  splitByWords,
} from '../_shared';

// "Paper & Marker" - crumpled-paper surface (SVG noise) with each title
// word stacked on its own line; the final word sits inside a rotated
// amber highlight block. Side arrow + page counter share wayfinding.

const PAPER = '#F0EDE6';
const AMBER = '#F4B53A';
const INK = '#0A0A0A';

function PaperGrain() {
  return (
    <svg
      className='pointer-events-none absolute inset-0 h-full w-full opacity-60 mix-blend-multiply'
      aria-hidden='true'
    >
      <defs>
        <filter id='t7-paper-noise'>
          <feTurbulence
            type='fractalNoise'
            baseFrequency='0.85'
            numOctaves='2'
            stitchTiles='stitch'
          />
          <feColorMatrix values='0 0 0 0 0.55  0 0 0 0 0.55  0 0 0 0 0.55  0 0 0 0.22 0' />
        </filter>
      </defs>
      <rect width='100%' height='100%' filter='url(#t7-paper-noise)' />
    </svg>
  );
}

function TopBar({ left, right }: { left?: string; right?: string }) {
  return (
    <div
      className={cn(
        'relative z-10 flex items-center justify-between font-bold',
        TYPE.topBar,
        PADDING.slideX,
        'pt-9 md:pt-12'
      )}
      style={{ color: INK }}
    >
      <SlideText className='truncate pr-4' text={left ?? ''} />
      {right && <span className='shrink-0 tabular-nums'>{right}</span>}
    </div>
  );
}

function BottomBar() {
  return (
    <div
      className={cn(
        'relative z-10 flex items-center gap-3',
        PADDING.slideX,
        'pb-9 md:pb-12'
      )}
    >
      <div
        className='h-[3px] w-12 shrink-0 md:w-16'
        style={{ backgroundColor: AMBER }}
        aria-hidden='true'
      />
      <div
        className='h-[3px] flex-1'
        style={{ backgroundColor: AMBER }}
        aria-hidden='true'
      />
    </div>
  );
}

function SideArrow({ side }: { side: 'left' | 'right' }) {
  return (
    <div
      className={cn(
        'pointer-events-none absolute z-10',
        side === 'left' ? 'left-6 md:left-8' : 'right-6 md:right-8',
        'top-1/2 -translate-y-1/2'
      )}
      aria-hidden='true'
    >
      <ArrowRight
        className='h-10 w-10 md:h-14 md:w-14'
        style={{ color: AMBER }}
        strokeWidth={3}
      />
    </div>
  );
}

// The title's words, one of them in the rotated amber block. A title with
// maths renders whole and unhighlighted: splitting it into words could cut
// through the expression.
function MarkerWords({
  title,
  words,
  highlightIndex,
  blockClass,
}: {
  title: string;
  words: string[];
  highlightIndex: number;
  blockClass: string;
}) {
  if (hasMath(title)) return <SlideText text={title} />;
  return (
    <>
      {words.map((word, i) => {
        const space = i === words.length - 1 ? null : ' ';
        if (i !== highlightIndex) {
          return (
            <span key={i}>
              {word}
              {space}
            </span>
          );
        }
        return (
          <span key={i}>
            <span
              className={cn('inline-block -rotate-[3deg]', blockClass)}
              style={{ backgroundColor: AMBER, color: INK }}
            >
              {word}
            </span>
            {space}
          </span>
        );
      })}
    </>
  );
}

// Normal flowing heading: the title wraps naturally; only the
// emphasized word gets the rotated amber highlight block, inline.
function MarkerTitle({
  title,
  emphasis = 'last',
  size,
}: {
  title: string;
  emphasis?: 'last' | 'first';
  size: 'cover' | 'body';
}) {
  const words = splitByWords(title);
  if (words.length === 0) return null;

  const highlightIndex = emphasis === 'first' ? 0 : words.length - 1;
  const Tag = size === 'cover' ? 'h1' : 'h2';

  return (
    <Tag
      className={cn(
        'text-balance font-extrabold uppercase tracking-tight',
        fitTitle(title, size),
        LEADING.display
      )}
      style={{ color: INK }}
    >
      <MarkerWords
        title={title}
        words={words}
        highlightIndex={highlightIndex}
        blockClass='px-2 py-0.5'
      />
    </Tag>
  );
}

function CoverLayout({
  card,
  slideIndex,
  totalSlides,
  conceptName,
}: SlideLayoutProps) {
  const showArrow = hasNextSlide(slideIndex, totalSlides);

  return (
    <div
      className='relative h-full w-full overflow-hidden'
      style={{ backgroundColor: PAPER }}
    >
      <PaperGrain />

      <div className='relative z-10 flex h-full w-full flex-col'>
        <TopBar left={conceptName} />

        <div className={cn('flex flex-1 items-center', PADDING.slideX)}>
          <MarkerTitle title={card.title} emphasis='first' size='cover' />
        </div>

        <BottomBar />
      </div>

      {showArrow && <SideArrow side='right' />}
    </div>
  );
}

function ContentLayout({
  card,
  slideIndex,
  totalSlides,
  conceptName,
}: SlideLayoutProps) {
  const showArrow = hasNextSlide(slideIndex, totalSlides);
  const showCounter = shouldShowCounter(slideIndex, totalSlides);
  const counter = showCounter
    ? formatPageLabel(slideIndex, totalSlides)
    : undefined;

  return (
    <div
      className='relative h-full w-full overflow-hidden'
      style={{ backgroundColor: PAPER }}
    >
      <PaperGrain />

      <div className='relative z-10 flex h-full w-full flex-col'>
        <TopBar left={conceptName} right={counter} />

        <div
          className={cn(
            'flex flex-1 flex-col justify-center gap-8',
            PADDING.slideX
          )}
        >
          <MarkerTitle title={card.title} emphasis='last' size='body' />

          {card.body && (
            <div className='max-w-xl'>
              <BodyContent
                body={card.body}
                visual={card.visual}
                style={{ color: INK }}
              />
            </div>
          )}
        </div>

        <BottomBar />
      </div>

      {showArrow && <SideArrow side='right' />}
    </div>
  );
}

function ThumbnailLayout({ card }: SlideLayoutProps) {
  return (
    <div
      className='relative h-full w-full overflow-hidden'
      style={{ backgroundColor: PAPER }}
    >
      <PaperGrain />

      <div className='relative z-10 flex h-full flex-col p-5'>
        <div className='flex flex-1 items-center'>
          <h3
            className={cn(
              'line-clamp-4 text-balance font-extrabold uppercase tracking-tight',
              fitTitle(card.title, 'thumb'),
              LEADING.display
            )}
            style={{ color: INK }}
          >
            <MarkerWords
              title={card.title}
              words={splitByWords(card.title)}
              highlightIndex={0}
              blockClass='px-1.5 py-0.5'
            />
          </h3>
        </div>

        <div className='flex items-center gap-2'>
          <div
            className='h-[3px] w-6 shrink-0'
            style={{ backgroundColor: AMBER }}
            aria-hidden='true'
          />
          <div
            className='h-[3px] flex-1'
            style={{ backgroundColor: AMBER }}
            aria-hidden='true'
          />
        </div>
      </div>
    </div>
  );
}

export const Template07: HighlightTemplate = {
  id: 'template-07',
  name: 'Paper & Marker',
  layouts: {
    cover: CoverLayout,
    body: ContentLayout,
    summary: ContentLayout,
  },
  thumbnail: ThumbnailLayout,
};
